import type { AuthUser } from "@dentacore/shared-types";

/**
 * Where the two tokens live, and why they live in different places.
 *
 * **The access token is in memory only.** It never touches storage, so a
 * closed tab takes it with it and nothing on disk carries a live credential.
 *
 * **The refresh token is in `localStorage`**, because otherwise a page reload
 * logs the user out — which, on a clinic screen someone reloads all day, is not
 * a security posture but an unusable one. The exposure is real and worth
 * stating plainly: any script that runs on this origin can read it.
 *
 * The stronger answer is an httpOnly, SameSite cookie the browser will not show
 * to JavaScript at all. It is not built here because it changes the API's
 * response shape and brings CSRF handling with it, and because this is the
 * skeleton story. It belongs to Phase 10 hardening, and it is recorded as such
 * rather than left to be rediscovered.
 */

/**
 * Exported because a cross-tab `storage` listener has to recognise this key by
 * name — the event says only which key changed, not what it meant.
 */
export const REFRESH_TOKEN_KEY = "dentacore.refreshToken";
/**
 * **What that key holds now** (OQ-D22): not the token, which is an httpOnly
 * cookie no script can read, but a marker that a session exists — so a cold
 * load knows to try a refresh, and another tab's `storage` event still says
 * the session changed. A real token found there (from before the cookie) is
 * sent once and replaced by this.
 */
export const SESSION_IN_COOKIE = "cookie";
const ACTIVE_BRANCH_KEY = "dentacore.activeBranchId";

/**
 * **Which user this tab believes it is acting as** — `sessionStorage`, so it is
 * per-tab and still survives that tab's own reload.
 *
 * `localStorage` has exactly one refresh-token slot per origin, and every clinic
 * reaches DentaCore at its own subdomain — so two staff accounts at the *same*
 * practice, open in two tabs, share that slot. Signing in as the second
 * overwrites the first, and because the access token lives only in memory, the
 * first tab adopts the new identity the moment it reloads. Nothing was wrong
 * with the token; it simply stopped being the same person's.
 *
 * That was reported from a running clinic: a tab open as one admin silently
 * became a colleague after a refresh. It matters more here than in most
 * products because clinical notes are append-only and attributed to whoever is
 * signed in — so the cost of the silent switch is a note written under the
 * wrong clinician's name, by someone who had no reason to look.
 *
 * **This does not try to give one browser two sessions.** It makes the tab able
 * to notice that the session underneath it changed, so the switch is announced
 * instead of silent. See `restoreSession` and `AuthProvider`.
 */
const SESSION_OWNER_KEY = "dentacore.sessionOwnerId";

let accessToken: string | null = null;
let currentUser: AuthUser | null = null;

export const tokens = {
  getAccessToken: (): string | null => accessToken,

  /** The user id this tab last acted as, or null if it has not acted yet. */
  getSessionOwner: (): string | null => {
    try {
      return sessionStorage.getItem(SESSION_OWNER_KEY);
    } catch {
      // Private browsing, or storage disabled. Null reads as "this tab has no
      // opinion yet", which is the permissive branch — the check can only ever
      // add a warning, never withhold a session it would otherwise have given.
      return null;
    }
  },

  /** Bind this tab to a user. Called wherever a session legitimately begins. */
  setSessionOwner: (userId: string): void => {
    try {
      sessionStorage.setItem(SESSION_OWNER_KEY, userId);
    } catch {
      // The tab simply keeps no opinion; see above.
    }
  },

  /**
   * Drop the binding while leaving the session alone — distinct from `clear`,
   * which ends the session too. A tab about to reload into whoever now holds
   * the stored token uses this, so that reload does not report the switch the
   * person has just accepted.
   */
  clearSessionOwner: (): void => {
    try {
      sessionStorage.removeItem(SESSION_OWNER_KEY);
    } catch {
      // Nothing to drop.
    }
  },

  getRefreshToken: (): string | null => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      // Private browsing, or storage disabled. Treated as signed out rather
      // than crashing the application on its first render.
      return null;
    }
  },

  getUser: (): AuthUser | null => currentUser,

  /**
   * Record who is signed in when the tokens did not arrive with them.
   *
   * `set` takes the user only alongside a fresh token pair, which is the login
   * path. **A cold reload restores the session from the stored refresh token
   * and never went through login**, so without this `currentUser` stayed null
   * for the whole life of that tab — which nothing noticed until `activeBranch`
   * needed the clinic id to key itself by.
   */
  setUser: (user: AuthUser): void => {
    currentUser = user;
  },

  set: (next: { accessToken: string; refreshToken?: string; user?: AuthUser }): void => {
    accessToken = next.accessToken;
    if (next.user !== undefined) {
      currentUser = next.user;
      // **A user arrives with the pair only when a session begins** — sign-in,
      // an invite spent, a sign-up hand-off. A plain token refresh calls this
      // without one. So this single line binds the tab at all three entry
      // points and at none of the refreshes, which is why the binding does not
      // live in the three callers.
      try {
        sessionStorage.setItem(SESSION_OWNER_KEY, next.user.id);
      } catch {
        // The tab keeps no opinion; the check degrades to permissive.
      }
    }
    try {
      // A marker, never the token (OQ-D22): the cookie holds that.
      localStorage.setItem(REFRESH_TOKEN_KEY, SESSION_IN_COOKIE);
    } catch {
      // Nothing to do: the session lives for as long as this tab does.
    }
  },

  clear: (): void => {
    accessToken = null;
    currentUser = null;
    try {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      // The remembered branch goes with the session that chose it. It is
      // already keyed by clinic, so this is the second of two guards rather
      // than the only one — but a signed-out browser holding a tenant's branch
      // id is a thing to leave behind, not to leave lying around.
      localStorage.removeItem(ACTIVE_BRANCH_KEY);
    } catch {
      // Already gone, as far as anything can tell.
    }
    try {
      // The tab's claim on an identity ends with the session that made it.
      // Left behind, the next sign-in in this tab would be compared against
      // whoever used it last and reported as a switch.
      sessionStorage.removeItem(SESSION_OWNER_KEY);
    } catch {
      // Same as above.
    }
  },
};

/**
 * The active branch, remembered across reloads so a reload does not dump
 * someone back at a branch picker. It is only ever a *hint*: the server checks
 * it against the caller's memberships on every request, so a stale or tampered
 * value gets a 403, never another branch's data.
 *
 * **It is stored against the clinic it belongs to, and in a multi-tenant
 * product that is not optional.** A branch id means nothing without its tenant:
 * one bare id in `localStorage` survived a sign-out, so signing out of one
 * clinic and into another left the next tenant's first requests carrying the
 * previous tenant's branch. Nothing leaked — `BranchScopeGuard` checks the
 * header against the caller's own memberships and refuses, and the switcher
 * corrects itself once its own fetch lands — but the window was real, every
 * screen mounting inside it was refused, and "each clinic gets its own origin
 * from its subdomain" is a property of production that local development, and
 * any future single-origin deployment, do not have. **Storing the clinic
 * alongside removes the assumption instead of relying on it.**
 *
 * A mismatch reads as `null`, which every consumer already handles as "not
 * yet" rather than as "none" — see `subscribe` below.
 */
const branchListeners = new Set<(branchId: string | null) => void>();

/** `{clinicId, branchId}`, or null for anything unreadable or malformed. */
function readStored(): { clinicId: string; branchId: string } | null {
  try {
    const raw = localStorage.getItem(ACTIVE_BRANCH_KEY);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    const { clinicId, branchId } = parsed as Record<string, unknown>;
    if (typeof clinicId !== "string" || typeof branchId !== "string") return null;

    return { clinicId, branchId };
  } catch {
    // Unreadable storage, or a bare id written by a build from before this was
    // keyed by clinic. Both mean "no remembered branch", which the switcher
    // already recovers from by picking the first branch the user can reach.
    return null;
  }
}

export const activeBranch = {
  get: (): string | null => {
    const stored = readStored();
    if (stored === null) return null;

    // No signed-in user means no clinic to match against, so a remembered
    // branch cannot be shown to belong to whoever is about to arrive.
    const clinicId = currentUser?.clinicId;
    if (clinicId === undefined || stored.clinicId !== clinicId) return null;

    return stored.branchId;
  },

  set: (branchId: string | null): void => {
    const clinicId = currentUser?.clinicId;

    try {
      if (branchId === null || clinicId === undefined) {
        localStorage.removeItem(ACTIVE_BRANCH_KEY);
      } else {
        localStorage.setItem(ACTIVE_BRANCH_KEY, JSON.stringify({ clinicId, branchId }));
      }
    } catch {
      // Same as above — the choice simply does not survive the tab.
    }

    for (const listener of branchListeners) listener(branchId);
  },

  /**
   * Be told when the active branch changes. Returns an unsubscribe.
   *
   * **This exists because "the branch is not known yet" is a real state, not an
   * edge case.** On a first sign-in nothing is in storage, and the branch
   * switcher only writes one once it has loaded the caller's branches — so a
   * branch-scoped screen mounting in between sends no branch header, gets
   * refused, and has nothing to retry on. Every such screen was silently
   * showing an empty list.
   *
   * `localStorage` fires no event in the tab that wrote it, so the notification
   * has to come from here.
   */
  subscribe: (listener: (branchId: string | null) => void): (() => void) => {
    branchListeners.add(listener);
    return () => branchListeners.delete(listener);
  },
};
